<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import {
  Role,
  InvitedUserInterface,
  UserInterface,
  RaterTypeNames,
  InviteUserInterface,
  AllRaterType,
} from "../../../interface/UserInterface";
import { appContainer } from "../../../container";
import { UserService } from "../../../services/userService";
import { debounce } from "../../../utils/debounce";
import { CompanyService } from "../../../services/manageCompanyService";
import { AxiosError } from "axios";
import { ErrorResponse } from "../../../interface/ErrorInterface";
import { LoadingService } from "../../../services/loadingService";
import { ToastService } from "../../../services/toastService";
import ValidationErrorComponent from "../../../components/ValidationErrorComponent.vue";
import { useRouter } from "vue-router";

const emit = defineEmits(["close", "edit"]);

const props = defineProps<{ user: InvitedUserInterface }>();

const userType = Role;
const userService = appContainer.resolve(UserService);
const companyService = appContainer.resolve(CompanyService);

const loadingService = appContainer.resolve(LoadingService);
const toastService = appContainer.resolve(ToastService);
const router = useRouter();

const userData = ref<InviteUserInterface>({
  first_name: "",
  last_name: "",
  email: "",
  invited_for: null,
  invitation_type: "",
  rater_type: null,
  duration_days: null,
  feedback_completion_date: null,
  admin_id: null,
});

const extendedDurationDays = ref<string>();
const validationErrors = ref<ErrorResponse>();

const invitationFor = ref<InvitedUserInterface | null>(null);
const raterTypeValue = ref<AllRaterType | null>(null);

const allAdmins = ref<InvitedUserInterface[]>([]);
const allParticipants = ref<InvitedUserInterface[]>([]);
const isLoading = ref(true);

onMounted(async () => {
  const loader = await loadingService.show();

  await initEditModal().then(() => {
    loader.hide();
    isLoading.value = false;
  });
});

async function initEditModal() {
  await getAllAdmins();
  await getAllParticipant();
  userData.value.first_name = props.user.invited_user.first_name;
  userData.value.last_name = props.user.invited_user.last_name;
  userData.value.email = props.user.invited_user.email;
  raterTypeValue.value = props.user.rater_type || null;
  userData.value.invitation_type = props.user.invitation_type;
  userData.value.duration_days = props.user.meta.duration_days;

  // Handle both Date and string formats
  const feedbackDate = props.user.meta.feedback_completion_date;

  if (feedbackDate) {
    const dateStr =
      typeof feedbackDate === "string"
        ? feedbackDate
        : feedbackDate.toISOString();
    const formattedDate = dateStr.split("T")[0];

    userData.value.feedback_completion_date = formattedDate;
    extendedDurationDays.value = formattedDate; // Also set for extend form
  } else {
    userData.value.feedback_completion_date = null;
    extendedDurationDays.value = undefined;
  }

  userData.value.admin_id = props.user.meta.admin_id;
  disabled.value = true;

  if (props.user.invitation_type === Role.Participants) {
    invitationFor.value = allAdmins.value.find(
      (item) => item.invited_user.id === props.user.invited_for_user?.id,
    ) as InvitedUserInterface;
  }
  if (props.user.invitation_type === Role.Raters) {
    invitationFor.value = allParticipants.value.find(
      (item) => item.invited_user?.id === props.user.invited_for_user?.id,
    ) as InvitedUserInterface;
  }
}

async function getAllAdmins() {
  const isAll = true;
  return userService
    .getInvitedUser({
      invitationType: Role.Admin,
      all: isAll,
      ...(props.user.company_id
        ? {
            companyId: props.user.company_id,
          }
        : {}),
      year: new Date().getFullYear(),
    })
    .then((result) => {
      allAdmins.value = result.items.filter(
        (item) => item.invitation_sent === true,
      );
    });
}

async function getAllParticipant() {
  const isAll = true;
  return userService
    .getInvitedUser({
      invitationType: Role.Participants,
      all: isAll,
      ...(props.user.company_id
        ? {
            companyId: props.user.company_id,
          }
        : {}),
      year: new Date().getFullYear(),
    })
    .then((result) => {
      allParticipants.value = result.items.filter(
        (item) => item.invitation_sent === true,
      );
    });
}

const existingUsers = ref<UserInterface[]>([]);

function updateInputFields() {
  disabled.value = false;
}

function fetchEmails() {
  if (userData.value.email.length === 0) {
    existingUsers.value = [];
    updateInputFields();
    return;
  }

  if (userData.value.email.length >= 3) {
    debounce(() => {
      const pageConfig = { page: 1, offset: 8 };
      userService
        .getUserWithRole("", true, pageConfig, userData.value.email)
        .then((result) => {
          existingUsers.value = result.items;
          showSuggestion.value = true;
        });
    }, 500)();

    if (existingUsers.value) {
      updateInputFields();
    }
  }
}
const showSuggestion = ref(false);
const hasAdminRole = ref(false);
const disabled = ref(false);
// const durationDays = DurationDays;

// Validation states
const formErrors = ref({
  email: "",
  first_name: "",
  invitation_type: "",
  invited_for: "",
  rater_type: "",
  feedbackCompletionDate: "",
});

const durationFormErrors = ref({
  extendedDurationDays: "",
});

function validateForm() {
  let isValid = true;
  formErrors.value = {
    email: "",
    first_name: "",
    invitation_type: "",
    invited_for: "",
    rater_type: "",
    feedbackCompletionDate: "",
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nameRegex = /^[A-Za-z]+$/;

  if (!userData.value.email) {
    formErrors.value.email = "Email is required.";
    isValid = false;
  } else if (!emailRegex.test(userData.value.email)) {
    formErrors.value.email = "Email is not valid.";
    isValid = false;
  }

  if (!userData.value.first_name) {
    formErrors.value.first_name = "First name is required.";
    isValid = false;
  } else if (!nameRegex.test(userData.value.first_name)) {
    formErrors.value.first_name = "First name can only contain letters.";
    isValid = false;
  }

  if (!userData.value.invitation_type) {
    formErrors.value.invitation_type = "Please select a user type.";
    isValid = false;
  }

  if (userData.value.invitation_type === Role.Participants) {
    if (!userData.value.feedback_completion_date) {
      formErrors.value.feedbackCompletionDate =
        "Please select the Assessment End Date.";
      isValid = false;
    } else {
      const selectedDate = new Date(userData.value.feedback_completion_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        formErrors.value.feedbackCompletionDate =
          "Assessment End Date must be today or a future date.";
        isValid = false;
      }
    }
  }

  if (
    userData.value.invitation_type === Role.Participants &&
    !invitationFor.value
  ) {
    formErrors.value.invited_for = "Please select an admin.";
    isValid = false;
  }

  if (userData.value.invitation_type === Role.Raters && !invitationFor.value) {
    formErrors.value.invited_for = "Please select a participant.";
    isValid = false;
  }

  if (userData.value.invitation_type === Role.Raters && !raterTypeValue.value) {
    formErrors.value.rater_type = "Please select a rater type.";
    isValid = false;
  }

  return isValid;
}

function durationValidateForm() {
  let isValid = true;
  durationFormErrors.value = {
    extendedDurationDays: "",
  };

  if (userData.value.invitation_type === Role.Participants) {
    if (!extendedDurationDays.value) {
      durationFormErrors.value.extendedDurationDays =
        "Please select the new Assessment End Date.";
      isValid = false;
    } else {
      const selectedDate = new Date(extendedDurationDays.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        durationFormErrors.value.extendedDurationDays =
          "Assessment End Date must be today or a future date.";
        isValid = false;
      }
    }
  }
  return isValid;
}

function setUserInfo(user: UserInterface) {
  userData.value.email = user.email;
  userData.value.first_name = user.first_name;
  userData.value.last_name = user.last_name;
  showSuggestion.value = false;
  disabled.value = true;
}

function closeDropdown() {
  setTimeout(() => {
    if (userData.value.email.length != 0) {
      const user = existingUsers.value.find(
        (user) => user.email == userData.value.email,
      );
      if (user) {
        setUserInfo(user as UserInterface);
      } else {
        updateInputFields();
        showSuggestion.value = false;
      }
    }
  }, 200);
}

async function emitEdit() {
  if (!validateForm()) {
    return;
  }

  if (invitationFor.value != null) {
    userData.value.invited_for = invitationFor.value.invited_user!.id;
  }
  if (raterTypeValue.value != null) {
    userData.value.rater_type = raterTypeValue.value;
  }

  const loader = await loadingService.show();
  if (props.user.company_id) {
    companyService
      .editInvitation(userData.value, props.user.company_id, props.user.id)
      .then(() => {
        emit("edit");
        toastService.success("User Information Updated Successfully");
      })
      .catch((err) => {
        const error = err as AxiosError;
        const errorResponse = error.response?.data as ErrorResponse;
        if (errorResponse?.errors) {
          const flattenedErrors = Object.values(errorResponse.errors).flat();
          toastService.error(flattenedErrors.join("<br>"));
        } else {
          toastService.error("Unable to update user");
        }
      })
      .finally(() => {
        loader.hide();
      });
  } else {
    userService
      .editInvitation(userData.value, props.user.id)
      .then(() => {
        emit("edit");
        toastService.success("User Information Updated Successfully");
      })
      .catch((err) => {
        const error = err as AxiosError;
        const errorResponse = error.response?.data as ErrorResponse;
        if (errorResponse?.errors) {
          const flattenedErrors = Object.values(errorResponse.errors).flat();
          toastService.error(flattenedErrors.join("<br>"));
        } else {
          toastService.error("Unable to update user");
        }
      })
      .finally(() => {
        loader.hide();
      });
  }
}

async function emitAddDuration() {
  if (!durationValidateForm()) {
    return;
  }
  const loader = await loadingService.show();
  userData.value.invited_for = props.user.invited_for_user?.id;
  if (props.user.company_id) {
    userService
      .extendFeedback(props.user.id, extendedDurationDays.value ?? "")
      .then(() => {
        emit("edit");
        toastService.success("Assessment End Date Updated Successfully");
      })
      .catch((err) => {
        const error = err as AxiosError;
        const errorResponse = error.response?.data as ErrorResponse;
        if (errorResponse?.errors) {
          const flattenedErrors = Object.values(errorResponse.errors).flat();
          toastService.error(flattenedErrors.join("<br>"));
        } else {
          toastService.error("Unable to Update Assessment End Date");
        }
      })
      .finally(() => {
        loader.hide();
      });
  }
}

async function setAdminId() {
  if (userData.value.invitation_type === Role.Raters) {
    userData.value.admin_id = invitationFor.value!.invited_for_user!.id;
  }
}
</script>
<template>
  <div class="modal-backdrop">
    <div class="modal fade show" id="add-modal">
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        v-if="!isLoading"
      >
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title fw-bold">
              Edit {{ props.user.company_id ? "Employee" : "User" }}
            </p>
            <button
              type="button"
              class="btn-close"
              @click="emit('close')"
            ></button>
          </div>
          <ValidationErrorComponent
            v-if="validationErrors"
            :validations="validationErrors"
          ></ValidationErrorComponent>
          <form
            v-if="!props.user.invitation_sent"
            @submit.prevent="emitEdit"
            class="w-100 overflow-y-auto"
          >
            <div class="modal-body">
              <div class="mb-3">
                <label for="editUserEmail" class="form-label fw-bold"
                  >Email</label
                >
                <div class="position-relative">
                  <input
                    id="editUserEmail"
                    type="email"
                    maxlength="50"
                    placeholder="Email"
                    class="form-control email"
                    v-model="userData.email"
                    :disabled="user.invitation_sent"
                    @input="fetchEmails"
                    @blur="closeDropdown"
                    data-cy=""
                  />
                  <div v-if="formErrors.email" class="text-danger mt-2 error">
                    {{ formErrors.email }}
                  </div>
                  <!-- <ul
                    class="position-absolute email-list list-unstyled"
                    v-show="showSuggestion"
                  >
                    <li
                      v-for="user in existingUsers"
                      :key="user.id"
                      :value="user.email"
                      @click="setUserInfo(user)"
                    >
                      {{ user.email }}
                    </li>
                  </ul> -->
                </div>
              </div>
              <div class="mb-3">
                <label for="editUserFirstName" class="form-label fw-bold"
                  >First Name</label
                >
                <input
                  id="editUserFirstName"
                  type="text"
                  placeholder="First Name"
                  class="form-control"
                  maxlength="14"
                  v-model="userData.first_name"
                  :disabled="!props.user.invited_user.is_editable"
                  data-cy=""
                />
                <div
                  v-if="formErrors.first_name"
                  class="text-danger mt-2 error"
                >
                  {{ formErrors.first_name }}
                </div>
              </div>
              <div class="mb-3">
                <label for="editUserLastName" class="form-label fw-bold"
                  >Last Name</label
                >
                <input
                  id="editUserLastName"
                  type="text"
                  placeholder="Last Name"
                  class="form-control"
                  v-model.trim="userData.last_name"
                  maxlength="14"
                  :disabled="!props.user.invited_user.is_editable"
                  data-cy=""
                />
              </div>
              <div
                class="mb-3"
                v-if="router.currentRoute.value.fullPath != '/admins'"
              >
                <label for="editUserType" class="form-label fw-bold"
                  >User Type</label
                >
                <select
                  id="editUserType"
                  class="form-select"
                  v-model="userData.invitation_type"
                  :class="{ 'is-invalid': formErrors.invitation_type }"
                >
                  <option value="">Please Select</option>
                  <template v-for="role in userType" :key="role">
                    <option :value="role" :disabled="hasAdminRole" data-cy="">
                      {{ role }}
                    </option>
                  </template>
                </select>
                <div
                  v-if="formErrors.invitation_type"
                  class="text-danger mt-2 error"
                >
                  {{ formErrors.invitation_type }}
                </div>
              </div>
              <template v-if="userData.invitation_type === Role.Participants">
                <div class="mb-3">
                  <label for="editSelectAdmin" class="form-label fw-bold"
                    >Select Admin</label
                  >
                  <select
                    id="editSelectAdmin"
                    class="form-select"
                    v-model="invitationFor"
                    :class="{ 'is-invalid': formErrors.invited_for }"
                    data-cy=""
                  >
                    <option :value="null">Please Select Admin</option>
                    <option
                      v-for="admin in allAdmins"
                      :key="admin.invited_user.id"
                      :value="admin"
                    >
                      {{ admin.invited_user.first_name }}
                      {{ admin.invited_user.last_name }}
                    </option>
                  </select>
                  <div
                    v-if="formErrors.invited_for"
                    class="text-danger mt-2 error"
                  >
                    {{ formErrors.invited_for }}
                  </div>
                </div>
                <div class="mb-3">
                  <label for="editDurationDays" class="form-label fw-bold"
                    >Assessment End Date</label
                  >
                  <VueDatePicker
                    id="editDurationDays"
                    v-model="userData.feedback_completion_date"
                    :min-date="new Date()"
                    :week-start="0"
                    :enable-time-picker="false"
                    placeholder="Please Select Assessment End Date"
                    format="MM-dd-yyyy"
                    model-type="yyyy-MM-dd"
                    auto-apply
                    text-input
                    :teleport="true"
                    input-class-name="form-control"
                    :time-config="{ enableTimePicker: false }"
                  />
                  <!-- <select class="form-select" v-model="userData.feedback_completion_date">
                    <option :value="null">
                      Please Select Days
                    </option>
                    <option
                      v-for="(duration, index) in durationDays"
                      :key="index"
                      :value="duration"
                    >
                      {{ duration }}
                    </option>
                  </select> -->
                  <div
                    v-if="formErrors.feedbackCompletionDate"
                    class="text-danger mt-2 error"
                  >
                    {{ formErrors.feedbackCompletionDate }}
                  </div>
                </div>
              </template>
              <template v-if="userData.invitation_type === Role.Raters">
                <div class="mb-3">
                  <label for="editSelectParticipant" class="form-label fw-bold"
                    >Select Participant</label
                  >
                  <select
                    id="editSelectParticipant"
                    class="form-select"
                    v-model="invitationFor"
                    :class="{ 'is-invalid': formErrors.invited_for }"
                    data-cy=""
                    @change="setAdminId"
                  >
                    <option :value="null">Please Select Participant</option>
                    <option
                      v-for="participant in allParticipants"
                      :key="participant.invited_user.id"
                      :value="participant"
                    >
                      {{ participant.invited_user.first_name }}
                      {{ participant.invited_user.last_name }} (
                      {{ participant.invited_for_user?.first_name }}
                      {{ participant.invited_for_user?.last_name }} )
                    </option>
                  </select>
                  <div
                    v-if="formErrors.invited_for"
                    class="text-danger mt-2 error"
                  >
                    {{ formErrors.invited_for }}
                  </div>
                </div>
                <div class="mb-3">
                  <label for="editRaterType" class="form-label fw-bold"
                    >Rater Type</label
                  >
                  <select
                    id="editRaterType"
                    class="form-select"
                    v-model="raterTypeValue"
                    :class="{ 'is-invalid': formErrors.rater_type }"
                    data-cy=""
                  >
                    <option :value="null">Please Select</option>
                    <template v-for="rater in RaterTypeNames" :key="rater">
                      <option :value="rater.value">
                        {{ rater.display_name }}
                      </option>
                    </template>
                  </select>
                  <div
                    v-if="formErrors.rater_type"
                    class="text-danger mt-2 error"
                  >
                    {{ formErrors.rater_type }}
                  </div>
                </div>
              </template>
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary btn-sm mx-auto text-white"
              >
                Update
              </button>
            </div>
          </form>
          <form
            v-else
            @submit.prevent="emitAddDuration"
            class="w-100 overflow-y-auto"
          >
            <div class="modal-body">
              <div class="mb-3">
                <label for="additionalDays" class="form-label fw-bold"
                  >Extend Assessment End Date</label
                >
                <VueDatePicker
                  id="additionalDays"
                  v-model="extendedDurationDays"
                  :min-date="new Date()"
                  :week-start="0"
                  :enable-time-picker="false"
                  placeholder="Select New End Date"
                  format="MM-dd-yyyy"
                  model-type="yyyy-MM-dd"
                  auto-apply
                  text-input
                  :teleport="true"
                  input-class-name="form-control"
                  :time-config="{ enableTimePicker: false }"
                />
                <div
                  v-if="durationFormErrors.extendedDurationDays"
                  class="text-danger mt-2 error"
                >
                  {{ durationFormErrors.extendedDurationDays }}
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary btn-sm mx-auto text-white"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
select {
  text-transform: capitalize;
}

ul {
  max-height: 280px;
  width: 100%;
  overflow-y: auto;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  li {
    padding: 4px 6px;
    cursor: pointer;
    &:hover {
      background-color: #00000050;
    }
  }
}

.error {
  color: red;
  font-size: 0.875rem;
}

.is-invalid {
  border-color: red;
}

/* Match VueDatePicker styling with Bootstrap form-control */
:deep(.dp__input) {
  border: 1px solid #dee2e6 !important;
  border-radius: 0.375rem !important;

  font-size: 1rem !important;
  line-height: 1.5 !important;
  color: #212529 !important;
  background-color: #fff !important;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out !important;
}

:deep(.dp__input:hover) {
  border-color: #dee2e6 !important;
}

:deep(.dp__input:focus) {
  border-color: #86b7fe !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
}

:deep(.dp__input_wrap) {
  width: 100% !important;
}
</style>
